import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  createProductReview,
  fetchProductReviews,
  hasPurchasedProduct,
} from '../lib/reviews';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import './ProductReviews.css';

function StarRating({ value, size = 'md', onSelect }) {
  const interactive = typeof onSelect === 'function';

  return (
    <div
      className={`review-stars review-stars--${size}${interactive ? ' review-stars--interactive' : ''}`}
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={`${value} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map(star => {
        const filled = star <= Math.round(value);
        if (!interactive) {
          return (
            <span key={star} className={filled ? 'filled' : ''}>
              ★
            </span>
          );
        }

        return (
          <button
            key={star}
            type="button"
            className={filled ? 'filled' : ''}
            aria-label={`${star} stars`}
            onClick={() => onSelect(star)}
          >
            ★
          </button>
        );
      })}
    </div>
  );
}

function ProductReviews({ productId, onStatsChange }) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);

  const alreadyReviewed = Boolean(
    user && reviews.some(review => review.user_id === user.id)
  );

  useEffect(() => {
    const urls = mediaFiles.map(file => ({
      url: URL.createObjectURL(file),
      isVideo: file.type.startsWith('video/'),
    }));
    setMediaPreviews(urls);

    return () => {
      urls.forEach(item => URL.revokeObjectURL(item.url));
    };
  }, [mediaFiles]);

  const load = async () => {
    if (!isSupabaseConfigured || !productId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await fetchProductReviews(productId);
      setReviews(data);
      onStatsChange?.(data);
    } catch (loadError) {
      setError(
        loadError.message?.includes('product_reviews')
          ? 'Reviews are not set up yet. Run supabase-reviews.sql in Supabase.'
          : 'Could not load reviews.'
      );
      setReviews([]);
      onStatsChange?.([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    let cancelled = false;

    async function checkEligibility() {
      if (!user || !productId || !isSupabaseConfigured) {
        setCanReview(false);
        setEligibilityChecked(true);
        return;
      }

      try {
        const purchased = await hasPurchasedProduct(productId);
        if (!cancelled) {
          setCanReview(purchased);
          setEligibilityChecked(true);
        }
      } catch {
        if (!cancelled) {
          setCanReview(false);
          setEligibilityChecked(true);
        }
      }
    }

    setEligibilityChecked(false);
    checkEligibility();

    return () => {
      cancelled = true;
    };
  }, [user, productId]);

  const handleFiles = fileList => {
    const next = Array.from(fileList || []).filter(
      file => file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    setMediaFiles(current => [...current, ...next].slice(0, 6));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!user || !canReview || alreadyReviewed) return;

    if (!body.trim()) {
      setError('Please write a review.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await createProductReview({
        productId,
        userId: user.id,
        rating,
        title: title.trim(),
        body: body.trim(),
        displayName:
          user.user_metadata?.full_name ||
          user.email?.split('@')[0] ||
          'Customer',
        mediaFiles,
      });

      setTitle('');
      setBody('');
      setRating(5);
      setMediaFiles([]);
      setShowForm(false);
      await load();
    } catch (submitError) {
      setError(
        submitError.message ||
          'Could not submit review. Make sure you have purchased this product.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="product-reviews" aria-labelledby="reviews-heading">
      <div className="product-reviews-header">
        <h2 id="reviews-heading">Reviews</h2>
        {eligibilityChecked && canReview && !alreadyReviewed && (
          <button
            type="button"
            className="product-reviews-write-btn"
            onClick={() => setShowForm(current => !current)}
          >
            {showForm ? 'Cancel' : 'Write a review'}
          </button>
        )}
      </div>

      {!user && (
        <p className="product-reviews-note">
          <Link to="/login" state={{ from: `/product/${productId}` }}>
            Sign in
          </Link>{' '}
          after purchasing to leave a review.
        </p>
      )}

      {user && eligibilityChecked && !canReview && (
        <p className="product-reviews-note">
          Only customers who have bought this product can leave a review.
        </p>
      )}

      {user && alreadyReviewed && (
        <p className="product-reviews-note">You have already reviewed this product.</p>
      )}

      {showForm && canReview && !alreadyReviewed && (
        <form className="product-review-form" onSubmit={handleSubmit}>
          <label className="product-review-label">
            Rating
            <StarRating value={rating} onSelect={setRating} size="lg" />
          </label>

          <label className="product-review-label">
            Title (optional)
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Sum up your experience"
            />
          </label>

          <label className="product-review-label">
            Review
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={4}
              required
              placeholder="How does it fit? Quality? Photos welcome."
            />
          </label>

          <div className="product-review-media-field">
            <button
              type="button"
              className="product-review-media-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              Add photos or videos
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={e => {
                handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
            {mediaPreviews.length > 0 && (
              <ul className="product-review-media-preview">
                {mediaPreviews.map((item, index) => (
                  <li key={`${item.url}-${index}`}>
                    {item.isVideo ? (
                      <video src={item.url} muted playsInline />
                    ) : (
                      <img src={item.url} alt="" />
                    )}
                    <button
                      type="button"
                      aria-label="Remove media"
                      onClick={() =>
                        setMediaFiles(current => current.filter((_, i) => i !== index))
                      }
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="submit"
            className="product-review-submit"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit review'}
          </button>
        </form>
      )}

      {error && <p className="product-reviews-error">{error}</p>}

      {loading ? (
        <p className="product-reviews-note">Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <p className="product-reviews-note">No reviews yet.</p>
      ) : (
        <ul className="product-reviews-list">
          {reviews.map(review => (
            <li key={review.id} className="product-review-card">
              <div className="product-review-card-top">
                <StarRating value={review.rating} />
                <span className="product-review-author">
                  {review.display_name || 'Customer'}
                </span>
                <time dateTime={review.created_at}>
                  {new Date(review.created_at).toLocaleDateString()}
                </time>
              </div>
              {review.title && <h3>{review.title}</h3>}
              <p>{review.body}</p>
              {review.media?.length > 0 && (
                <div className="product-review-media-grid">
                  {review.media.map(item =>
                    item.media_type === 'video' ? (
                      <video
                        key={item.id}
                        src={item.media_url}
                        controls
                        playsInline
                      />
                    ) : (
                      <img
                        key={item.id}
                        src={item.media_url}
                        alt=""
                      />
                    )
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default ProductReviews;
